// components/CalculatorModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { textOnlyOptions } from "../lib/countries";

export default function CalculatorModal({ calculator = "", onClose = () => {} }) {
  const [isOpen, setOpen] = useState(false);
  const [og, setOg] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const openedRef = useRef(false); // avoid opening twice

  // read query params from search or hash (? after #)
  function getQueryParams() {
    const qs = new URLSearchParams(window.location.search || "");
    if (qs.toString()) return qs;
    const h = window.location.hash || "";
    const i = h.indexOf("?");
    return new URLSearchParams(i >= 0 ? h.slice(i + 1) : "");
  }

  useEffect(() => {
    function openWith(payload = {}) {
      if (openedRef.current) return;
      openedRef.current = true;
      setOg(payload || {});
      setOpen(true);
      setSubmitted(false);
      setError("");
    }

    // 1) Open via Outgrow completion redirect (strict)
    const qp = getQueryParams();
    const ogComplete = qp.get("og_complete") === "1";
    const ogOpen = qp.get("og_open") === "1";
    if (ogComplete && ogOpen) {
      openWith({
        outgrow_event: "redirect_complete",
        outgrowUrl: window.location.href,
        lead_id: qp.get("lead_id") || qp.get("leadId") || "",
        visitor_key: qp.get("visitor_key") || qp.get("visitorKey") || "",
        result: qp.get("result") || "",
        utm_source: qp.get("utm_source") || "",
        utm_medium: qp.get("utm_medium") || "",
        utm_campaign: qp.get("utm_campaign") || "",
        utm_term: qp.get("utm_term") || "",
        utm_content: qp.get("utm_content") || "",
      });
    }

    // 2) Open via Outgrow postMessage (strict: must have BOTH lead_id & result)
    function onMessage(ev) {
      try {
        const host = new URL(ev.origin).hostname;
        if (!/\.outgrow\.(co|us)$/i.test(host)) return;
      } catch {
        return;
      }
      let d = ev.data;
      if (typeof d === "string") { try { d = JSON.parse(d); } catch {} }
      if (!d || typeof d !== "object") return;

      const lead_id = d.lead_id || d.leadId || "";
      const result  = d.result || "";
      if (!lead_id || !result) return; // strict = only on completion

      openWith({
        outgrow_event: d.outgrow_event || d.eventType || d.event || "message_complete",
        outgrowUrl: d.outgrow_url || d.outgrowUrl || "",
        lead_id,
        visitor_key: d.visitor_key || d.visitorKey || "",
        result,
        utm_source: d.utm_source || "",
        utm_medium: d.utm_medium || "",
        utm_campaign: d.utm_campaign || "",
        utm_term: d.utm_term || "",
        utm_content: d.utm_content || "",
        answers_json:
          typeof d.answers_json === "string"
            ? d.answers_json
            : d.answers
            ? JSON.stringify(d.answers)
            : "",
      });
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const f = e.currentTarget;

    const name           = String(f.name.value || "").trim();
    const email          = String(f.email.value || "").trim();
    const countryCode    = String(f.countryCode.value || "");
    const phoneLocal     = String(f.phoneLocal.value || "").trim();
    const readyToInvest  = String(f.readyToInvest.value || "");
    const amountToInvest = String(f.amountToInvest.value || "");
    const message        = String(f.message.value || "").trim();
    const consent        = f.consent.checked ? "yes" : "no";

    // map Outgrow context
    const outgrowUrl = og?.outgrowUrl || "";
    const leadId = og?.lead_id || "";
    const visitorKey = og?.visitor_key || "";
    const result = og?.result || "";
    let answers = undefined;
    try { if (og?.answers_json) answers = JSON.parse(og.answers_json); } catch {}

    try {
      const payload = {
        source: "calculator",
        calculator,

        // context
        outgrow_event: og?.outgrow_event || "",
        eventType: og?.eventType || "",
        outgrowUrl,
        outgrow_url: outgrowUrl,
        url: outgrowUrl,
        lead_id: leadId,
        leadId,
        visitor_key: visitorKey,
        visitorKey,
        result,
        answers_json: answers ? JSON.stringify(answers) : "",
        answers: answers || undefined,

        // user info
        name,
        email,
        countryCode,
        phone: phoneLocal,
        phoneLocal,
        message,
        readyToInvest,
        amountToInvest,
        consent,
        timestamp: new Date().toISOString(),
      };

      // client debug (optional)
      console.log("[CALC MODAL] submit keys:", Object.keys(payload));
      console.log("[CALC MODAL] ready/amount:", { readyToInvest, amountToInvest });

      const res = await fetch("/enquire", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: new URLSearchParams(payload),
      });

      const txt = await res.text();
      let ok = res.ok;
      try {
        const j = JSON.parse(txt);
        if (typeof j?.ok === "boolean") ok = j.ok;
        if (j?.error) throw new Error(j.error);
      } catch {}
      if (!ok) throw new Error("Submission failed");

      window.gtag?.("event", "generate_lead", { form_name: "calculator" });
      setSubmitted(true);
      f.reset();
    } catch (err) {
      setError(err?.message || "Submission error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Share details to get your calculator results</h2>
          <button
            type="button"
            className="px-2 py-1 rounded border"
            onClick={() => { setOpen(false); onClose(); }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {submitted ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[#003366]">
            <h3 className="text-lg font-semibold">Thanks — your results are on their way</h3>
            <p className="mt-1 text-slate-700">One of our team will be in touch over the next few days to discuss in more detail and to see how we can help you achieve your goals. If you’d like to discuss the numbers sooner, you can book a quick call.</p>
            <div className="mt-3 flex gap-3">
               <a
    href="https://wa.me/442038852754?text=Hi%2C%20I%E2%80%99d%20like%20to%20discuss%20my%20investment%20plans."
    target="_blank" rel="noreferrer"
    className="inline-flex items-center rounded bg-[#003366] text-white px-4 py-2"
  >
    Chat on WhatsApp
  </a>
              <button className="rounded-xl border border-slate-300 px-4 py-2 text-[#003366] hover:bg-slate-50" onClick={()=>{ setOpen(false); onClose(); }}>Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Name</label>
              <input name="name" type="text" required autoComplete="name" className="mt-1 w-full rounded border px-3 py-2" disabled={loading} />
            </div>
            <div>
              <label className="block text-sm font-medium">Email</label>
              <input name="email" type="email" required autoComplete="email" className="mt-1 w-full rounded border px-3 py-2" disabled={loading} />
            </div>
            <div>
              <label className="block text-sm font-medium">Phone</label>
              <div className="mt-1 flex gap-2">
                <select name="countryCode" required defaultValue="+44" className="rounded border px-3 py-2" disabled={loading}>
                  {textOnlyOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input name="phoneLocal" required inputMode="tel" autoComplete="tel" className="flex-1 rounded border px-3 py-2" disabled={loading} />
              </div>
            </div>

            <div>
              <label className="block text-sm">When are you planning to make your next investment?</label>
              <select name="readyToInvest" required className="w-full rounded border px-3 py-2" defaultValue="" disabled={loading}>
                <option value="" disabled>Select an option…</option>
                <option>Ready to invest now.</option>
                <option>Within 3 months.</option>
                <option>3-6 months.</option>
                <option>Just exploring for now.</option>
              </select>
            </div>

            <div>
              <label className="block text-sm">What level of funds are you planning to allocate?</label>
              <select name="amountToInvest" required className="w-full rounded border px-3 py-2" defaultValue="" disabled={loading}>
                <option value="" disabled>Select an option…</option>
                <option>Under £100,000</option>
                <option>£100,000 – £250,000</option>
                <option>£250,000 - £500,000</option>
                <option>£500,000 plus</option>
                <option>Uknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium">Message (optional)</label>
              <textarea name="message" rows="4" className="mt-1 w-full rounded border px-3 py-2" disabled={loading} />
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input name="consent" type="checkbox" className="mt-1" required disabled={loading} />
              <span>I agree to be contacted about related services and have read the privacy notice.</span>
            </label>

            {error && <div className="text-red-600 font-medium">{error}</div>}

            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { setOpen(false); onClose(); }} className="rounded border px-4 py-2">Cancel</button>
              <button type="submit" disabled={loading} className="rounded bg-[#003366] text-white px-5 py-2">{loading ? "Sending…" : "Send"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
